import { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { Button, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { Close } from "@mui/icons-material";
import { Check, X } from "react-feather";
import { authData } from "../../../components/GetAuth";
import popupStyles from "../../../components/popups/Popup.module.css";
import styles from "../shop.module.css";

const REASON_LABEL = { restock: "Restock", adjustment: "Adjustment", order: "Order", cancel: "Order cancelled" };

// Add / remove / set stock for one shop item, with its movement history underneath
const StockAdjustPopup = ({ item, onClose, onSaved, notify }) => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const [form, setForm] = useState({ mode: "add", quantity: "", reason: "restock", note: "" });
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(process.env.REACT_APP_API_URL + "v1/shop/inventory/" + item.id + "/history", {
      headers: { Authorization: `Bearer ${API_TOKEN}` },
      params: { per_page: 20 },
    }).then((response) => {
      if (response.data.status === "SUCCESS") {
        setHistory(response.data.data.data || []);
      }
    }).catch(() => { });
  }, [item.id, API_TOKEN]);

  const current = parseInt(item.stock_qty) || 0;
  const qty = parseInt(form.quantity) || 0;
  const after = form.mode === "add" ? current + qty : form.mode === "remove" ? current - qty : qty;

  const save = () => {
    setBusy(true);
    axios.post(process.env.REACT_APP_API_URL + "v1/shop/inventory/" + item.id + "/adjust", { ...form, quantity: qty, track_stock: 1 }, {
      headers: { Authorization: `Bearer ${API_TOKEN}` },
    }).then((response) => {
      setBusy(false);
      if (response.data.status === "SUCCESS") {
        notify("Stock updated.", "success");
        onSaved(response.data.data);
      } else {
        notify(response.data.message, "error");
      }
    }).catch(() => {
      setBusy(false);
      notify("Something went wrong. Please try again.", "error");
    });
  };

  return (<>
    <DialogTitle className={`${popupStyles.PopupTitleBar}`}>
      Stock: {item.title}
      <IconButton onClick={onClose} className={`${popupStyles.PopupCloseBU}`} aria-label="Close"><Close /></IconButton>
    </DialogTitle>
    <DialogContent className={`${popupStyles.PopupContent}`}>
      <p className={`${popupStyles.PopupTitle}`}>In stock now: <strong>{parseInt(item.track_stock) === 1 ? current : "not tracked"}</strong>{parseInt(item.track_stock) !== 1 && " (saving will start tracking stock for this item)"}</p>
      <div className={`${popupStyles.FormMainRow}`}>
        <div className={`${popupStyles.FormGroupHalf}`}>
          <FormControl fullWidth>
            <InputLabel>Change</InputLabel>
            <Select label="Change" value={form.mode} onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value, reason: e.target.value === "add" ? "restock" : "adjustment" }))}>
              <MenuItem value="add">Add stock</MenuItem>
              <MenuItem value="remove">Remove stock</MenuItem>
              <MenuItem value="set">Set stock to</MenuItem>
            </Select>
          </FormControl>
        </div>
        <div className={`${popupStyles.FormGroupHalf}`}>
          <TextField label="Quantity*" type="number" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} inputProps={{ min: 0 }} fullWidth />
          {form.quantity !== "" && <span className={`${after < 0 ? popupStyles.ErrorM : popupStyles.HintText}`}>New stock: {after}</span>}
        </div>
        <div className={`${popupStyles.FormGroupHalf}`}>
          <FormControl fullWidth>
            <InputLabel>Reason</InputLabel>
            <Select label="Reason" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}>
              <MenuItem value="restock">Restock</MenuItem>
              <MenuItem value="adjustment">Adjustment (count, damage, loss)</MenuItem>
            </Select>
          </FormControl>
        </div>
        <div className={`${popupStyles.FormGroupHalf}`}>
          <TextField label="Note" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} fullWidth />
        </div>
      </div>
      <div className={`${popupStyles.ButtonAction}`}>
        <Button className={`${popupStyles.CancelPopupBU}`} startIcon={<X size={16} />} onClick={onClose}>Cancel</Button>
        <Button className={`${popupStyles.SubmitPopupBU}`} startIcon={<Check size={16} />} disabled={busy || form.quantity === "" || after < 0} onClick={save}>Save</Button>
      </div>

      <h4 style={{ margin: "20px 0 8px" }}>Recent stock history</h4>
      {history.length === 0 && <p className={`${styles.SubText}`}>No stock changes yet.</p>}
      {history.length > 0 && (<table className={`${styles.ItemsTable}`}>
        <thead>
          <tr><th>Date</th><th>Reason</th><th className={`${styles.Right}`}>Change</th><th className={`${styles.Right}`}>Stock after</th><th>By / note</th></tr>
        </thead>
        <tbody>
          {history.map((h) => (<tr key={h.id}>
            <td>{dayjs(h.created_at).format("DD/MM/YYYY HH:mm")}</td>
            <td>{REASON_LABEL[h.reason] || h.reason}</td>
            <td className={`${styles.Right}`} style={{ color: h.change < 0 ? "#dc2626" : "#16a34a" }}>{h.change > 0 ? `+${h.change}` : h.change}</td>
            <td className={`${styles.Right}`}>{h.balance}</td>
            <td>{h.user?.name || "-"}{h.note ? ` · ${h.note}` : ""}</td>
          </tr>))}
        </tbody>
      </table>)}
    </DialogContent>
  </>);
};

export default StockAdjustPopup;
